import { INDIA_STATES } from './indiaStates';
import type { IndiaStateCode } from './types';

/** Collapse spaces/punct for alias lookup. */
function key(s: string): string {
  return s
    .normalize('NFKC')
    .toUpperCase()
    .replace(/&/g, ' AND ')
    .replace(/[^A-Z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const ALIASES: Record<string, IndiaStateCode> = {
  // Official / common amazon StateOrRegion strings
  'ANDAMAN AND NICOBAR ISLANDS': 'AN',
  ANDAMAN: 'AN',
  'ANDHRA PRADESH': 'AP',
  AP: 'AP',
  'ARUNACHAL PRADESH': 'AR',
  ASSAM: 'AS',
  BIHAR: 'BR',
  CHANDIGARH: 'CH',
  CHHATTISGARH: 'CT',
  CHATTISGARH: 'CT',
  // CG → CT only once below
  'DADRA AND NAGAR HAVELI AND DAMAN AND DIU': 'DH',
  'DADRA AND NAGAR HAVELI': 'DH',
  'DAMAN AND DIU': 'DH',
  DNHDD: 'DH',
  DELHI: 'DL',
  'NEW DELHI': 'DL',
  'NCT OF DELHI': 'DL',
  'NATIONAL CAPITAL TERRITORY OF DELHI': 'DL',
  GOA: 'GA',
  GUJARAT: 'GJ',
  'HIMACHAL PRADESH': 'HP',
  HARYANA: 'HR',
  JHARKHAND: 'JH',
  'JAMMU AND KASHMIR': 'JK',
  'JAMMU & KASHMIR': 'JK',
  KARNATAKA: 'KA',
  KERALA: 'KL',
  LADAKH: 'LA',
  LAKSHADWEEP: 'LD',
  MAHARASHTRA: 'MH',
  MEGHALAYA: 'ML',
  MANIPUR: 'MN',
  'MADHYA PRADESH': 'MP',
  MIZORAM: 'MZ',
  NAGALAND: 'NL',
  ODISHA: 'OD',
  ORISSA: 'OD',
  PUNJAB: 'PB',
  PONDICHERRY: 'PY',
  PUDUCHERRY: 'PY',
  RAJASTHAN: 'RJ',
  SIKKIM: 'SK',
  TELANGANA: 'TG',
  'TAMIL NADU': 'TN',
  TAMILNADU: 'TN',
  TRIPURA: 'TR',
  'UTTAR PRADESH': 'UP',
  UTTARAKHAND: 'UT',
  UTTARANCHAL: 'UT',
  'WEST BENGAL': 'WB',
  // ISO-ish short codes
  AN: 'AN',
  AR: 'AR',
  AS: 'AS',
  BR: 'BR',
  CH: 'CH',
  CT: 'CT',
  CG: 'CT',
  DN: 'DH',
  DD: 'DH',
  DH: 'DH',
  DL: 'DL',
  GA: 'GA',
  GJ: 'GJ',
  HP: 'HP',
  HR: 'HR',
  JH: 'JH',
  JK: 'JK',
  KA: 'KA',
  KL: 'KL',
  LA: 'LA',
  LD: 'LD',
  MH: 'MH',
  ML: 'ML',
  MN: 'MN',
  MP: 'MP',
  MZ: 'MZ',
  NL: 'NL',
  OD: 'OD',
  OR: 'OD',
  PB: 'PB',
  PY: 'PY',
  RJ: 'RJ',
  SK: 'SK',
  TG: 'TG',
  TS: 'TG',
  TN: 'TN',
  TR: 'TR',
  UP: 'UP',
  UT: 'UT',
  UK: 'UT',
  WB: 'WB',
};

// Register names as aliases too
for (const s of INDIA_STATES) {
  ALIASES[key(s.name)] = s.code;
  ALIASES[s.code] = s.code;
}

/**
 * Map a LIVE ship_state string to an official India code.
 * Returns null when unknown — never invents a state.
 */
export function normalizeIndiaState(raw: string | null | undefined): IndiaStateCode | null {
  if (raw == null) return null;
  const t = String(raw).trim();
  if (!t) return null;
  const k = key(t);
  if (!k) return null;
  if (ALIASES[k]) return ALIASES[k];
  // Compact key (no spaces)
  const compact = k.replace(/\s+/g, '');
  if (ALIASES[compact]) return ALIASES[compact];
  // 2-letter code
  if (/^[A-Z]{2}$/.test(k) && ALIASES[k]) return ALIASES[k];
  return null;
}
